"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/db/auth/client";
import { toast } from "@garagem/ui/components/sonner";
import { authQueryKeys } from "./query-keys";

// User Authentication Hooks
export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { email: string; password: string }) => {
      const { data, error } = await authClient.signIn.email(params);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all auth-related queries after sign in
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
    },
  });
}

export function useSignUp() {
  return useMutation({
    mutationFn: async (params: {
      email: string;
      password: string;
      name: string;
      phone: string;
      acceptTerms: true;
      acceptPrivacy: true;
      acceptAiTransparency: true;
    }) => {
      const { data, error } = await authClient.signUp.email(params);
      if (error) throw error;
      return data;
    },
  });
}

export function useVerifyBackupCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { code: string }) => {
      const { data, error } =
        await authClient.twoFactor.verifyBackupCode(params);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all auth-related queries after backup code verification
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      password,
      confirmation,
    }: {
      password: string;
      confirmation: string;
    }) => {
      const response = await fetch("/api/auth/delete-account", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password,
          confirmation,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to delete account");
      }

      return data;
    },
    onSuccess: async () => {
      // Sign out and clear all cached data
      await authClient.signOut();
      queryClient.clear();
      // Redirect to home page
      window.location.href = "/";
    },
    onError: (error) => {
      toast.error("Falha ao excluir conta", {
        description: error?.message || "Ocorreu um erro ao excluir sua conta.",
      });
    },
  });
}

